import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Container,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
  useTheme,
  Snackbar,
  Alert,
  Button,
  Fade,
  Zoom,
  useMediaQuery,
  Tabs,
  Tab,
  styled,
  Chip,
} from "@mui/material";

// Icons
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import Scrollbar from "src/components/scrollbar";
import GradientText from "src/components/GradientText";

import useChatLogic from "./useChatLogic";
import MessageCard from "./MessageCard";
import InputBar from "./InputBar";
import EnhancedProgressDisplay from "./EnhancedProgressDisplay";
import ThinkingModeDisplay from "./ThinkingModeDisplay";

import AnalysisModal from "./AnalysisModal";
import ArcSearch from "./ArcSearch";
import { NAV } from "src/layouts/dashboard/config-layout";
import { TopLoader } from "./saanvi-styles";

// --- Styled Components ---

// 1. Pill Tabs (Floating)
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  minHeight: 40,
  zIndex: 1,
  borderRadius: "20px",
  transition: "all 0.3s ease",
  color: theme.palette.text.secondary,
  "&.Mui-selected": {
    color: theme.palette.text.primary,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    boxShadow: theme.customShadows?.z8 || "0 4px 12px rgba(0,0,0,0.08)",
  },
}));

export default function SaanviChat({
  authToken,
  initialPrompt = null,
  showInitialPrompt = false,
  isAuthenticated = false,
  isModal = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // --- State ---
  const [activeTab, setActiveTab] = useState(0); // 0: Chat, 1: ArcSearch
  const [hasArcResults, setHasArcResults] = useState(false);

  // REMOVED: thinkingCapacity state and settingsAnchor (now inside ArcSearch)

  const {
    chat,
    message,
    setMessage,
    loading,
    loadingMessage,
    inCommingMessage,
    deepAnalysis,
    deepDive,
    deeperDive,
    selectedFiles,
    elapsedTime,
    progressUpdates,
    scrollRef,
    messagesEndRef,
    thinkingMode,
    handleSendMessage,
    handleKeyDown,
    handleFileSelect,
    handleRemoveFile,
    stopGeneration,
    clearChat,
    handleDeepAnalysisToggle,
    handleDeepDiveToggle,
    handleDeeperDiveToggle,
    handleThinkingModeToggle,
  } = useChatLogic(
    authToken,
    initialPrompt,
    showInitialPrompt,
    isAuthenticated
  );

  // UI state
  const [copied, setCopied] = useState(false);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [fileMenuAnchor, setFileMenuAnchor] = useState(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [error, setError] = useState({
    open: false,
    message: "",
    isServerError: false,
  });

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // --- Handlers ---

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  // Standard chat send
  const handleChatSend = async (text, files) => {
    await handleSendMessage(text, files);
  };

  const handleCopy = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleOpenAnalysisModal = useCallback(
    () => !loading && setAnalysisModalOpen(true),
    [loading]
  );
  const handleCloseAnalysisModal = useCallback(
    () => setAnalysisModalOpen(false),
    []
  );
  const handleFileMenuOpen = useCallback(
    (event) => setFileMenuAnchor(event.currentTarget),
    []
  );
  const handleFileMenuClose = useCallback(() => setFileMenuAnchor(null), []);

  const handleCameraClick = useCallback(() => {
    cameraInputRef.current?.click();
    handleFileMenuClose();
  }, [handleFileMenuClose]);
  const handleGalleryClick = useCallback(() => {
    galleryInputRef.current?.click();
    handleFileMenuClose();
  }, [handleFileMenuClose]);

  const handleImageClick = useCallback((imageUrl) => {
    setViewerImage(imageUrl);
    setImageViewerOpen(true);
  }, []);
  const handleImageViewerClose = useCallback(() => {
    setImageViewerOpen(false);
    setViewerImage(null);
  }, []);
  const handleCloseError = useCallback(
    () => setError({ open: false, message: "", isServerError: false }),
    []
  );

  return (
    <Container
      maxWidth="xl"
      disableGutters={isMobile}
      sx={{
        height: isModal
          ? "100vh"
          : `calc(100vh - ${isMobile ? NAV.MOBILE : 0}px)`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* --- Header --- */}
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{
          flexShrink: 0,
          zIndex: 1100,
          background: "transparent",
          border: "none",
        }}
      >
        <Toolbar
          sx={{ minHeight: "80px !important", px: isMobile ? 2 : 3, gap: 2 }}
        >
          {/* Left: Logo */}
          <Box sx={{ width: "150px", display: "flex", alignItems: "center" }}>
            <Box
              component="img"
              src="/assets/saanvi_logo.png"
              alt="SAANVI"
              height="24px"
              width="auto"
              sx={{
                objectFit: "contain",
                display: isMobile ? "none" : "block",
              }}
            />
          </Box>

          {/* Center: Tabs */}
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                p: 0.5,
                borderRadius: "30px",
                backgroundColor: alpha(theme.palette.grey[500], 0.08),
                backdropFilter: "blur(12px)",
                border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  minHeight: 40,
                  "& .MuiTabs-indicator": { display: "none" },
                  "& .MuiTabs-flexContainer": { gap: 1 },
                }}
              >
                <StyledTab label="Chat" disableRipple />
                <StyledTab label="Arc Search" disableRipple />
              </Tabs>
            </Box>
          </Box>

          {/* Right: Actions */}
          <Box
            sx={{
              width: "150px",
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            {/* REMOVED: Capacity Button (logic moved to ArcSearch component) */}

            {activeTab === 0 && (
              <Button
                onClick={clearChat}
                startIcon={<RestartAltRoundedIcon />}
                color="secondary"
                variant="outlined"
                size="small"
                disabled={loading || chat.length === 0}
                sx={{
                  whiteSpace: "nowrap",
                  borderRadius: "20px",
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  backdropFilter: "blur(8px)",
                }}
              >
                New Chat
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* --- Main Content --- */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {activeTab === 0 ? (
          // ================= CHAT INTERFACE =================
          <>
            <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
              {loading && <TopLoader />}
              <Scrollbar
                scrollableNodeProps={{ ref: scrollRef }}
                sx={{ height: "100%" }}
              >
                <Container
                  maxWidth="md"
                  sx={{
                    py: 3,
                    minHeight: "50dvh",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: chat.length === 0 ? "center" : "flex-start",
                  }}
                >
                  <Stack spacing={2}>
                    {/* Welcome State */}
                    {chat.length === 0 && !loading && (
                      <Fade in timeout={600}>
                        <Stack
                          alignItems="center"
                          justifyContent="center"
                          sx={{ minHeight: "100%", py: 4 }}
                        >
                          <Box sx={{ mb: 4, textAlign: "center" }}>
                            <GradientText
                              variant="h4"
                              sx={{
                                fontWeight: 700,
                                mb: 1,
                                display: "inline-block",
                              }}
                            >
                              Hello, I'm Saanvi
                            </GradientText>
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              sx={{ maxWidth: 600, mx: "auto", mb: 1 }}
                            >
                              What do you want to know today?
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              I have access to real-time news and advanced
                              analysis tools.
                            </Typography>
                          </Box>

                          {/* Mode Alert */}
                          {(deepAnalysis || deepDive || deeperDive) && (
                            <Zoom in timeout={200}>
                              <Alert
                                severity="info"
                                icon={<AutoAwesomeIcon />}
                                sx={{
                                  maxWidth: 500,
                                  alignItems: "center",
                                  fontSize: "0.95rem",
                                }}
                              >
                                You are in{" "}
                                <strong>
                                  {deeperDive
                                    ? "Deeper Dive"
                                    : deepDive
                                    ? "Deep Dive"
                                    : "Deep Analysis"}
                                </strong>{" "}
                                mode.
                                {deeperDive
                                  ? " Accessing live web data."
                                  : " Performing enhanced reasoning."}
                              </Alert>
                            </Zoom>
                          )}
                        </Stack>
                      </Fade>
                    )}

                    {/* Messages */}
                    {chat.map((msg, idx) => (
                      <MessageCard
                        key={idx}
                        message={msg}
                        index={idx}
                        onCopy={() => handleCopy(msg.content)}
                        copied={copied}
                        onImageClick={handleImageClick}
                      />
                    ))}

                    {loading && thinkingMode && (
                      <ThinkingModeDisplay progressUpdates={progressUpdates} />
                    )}

                    {/* Loading */}
                    {loading && (
                      <Box>
                        {(deepAnalysis || deepDive || deeperDive) && (
                          <EnhancedProgressDisplay
                            progressUpdates={progressUpdates}
                            mode={
                              deeperDive
                                ? "deeper_dive"
                                : deepDive
                                ? "deep_dive"
                                : "deep_analysis"
                            }
                          />
                        )}
                        {/* Streaming message */}
                        {loading && inCommingMessage && (
                          <MessageCard
                            message={{
                              role: "assistant",
                              content: inCommingMessage,
                            }}
                            index={chat.length}
                            isStreaming
                          />
                        )}
                        {!inCommingMessage && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              py: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="primary"
                              fontWeight={600}
                            >
                              {loadingMessage}
                            </Typography>
                            {elapsedTime && (
                              <Chip
                                label={elapsedTime}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </Stack>
                </Container>
              </Scrollbar>
            </Box>

            {/* Input Area */}
            <Container maxWidth="lg" sx={{ py: 2, flexShrink: 0 }}>
              <InputBar
                message={message}
                setMessage={setMessage}
                loading={loading}
                deepAnalysis={deepAnalysis}
                deepDive={deepDive}
                deeperDive={deeperDive}
                selectedFiles={selectedFiles}
                onSend={handleChatSend}
                onStop={stopGeneration}
                onKeyDown={handleKeyDown}
                onFileSelect={handleFileSelect}
                onRemoveFile={handleRemoveFile}
                onOpenAnalysisModal={handleOpenAnalysisModal}
                onOpenFileMenu={handleFileMenuOpen}
                onThinkingModeToggle={handleThinkingModeToggle}
                thinkingMode={thinkingMode}
              />
            </Container>
          </>
        ) : (
          // ================= ARC SEARCH INTERFACE =================
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: hasArcResults ? "flex-start" : "center",
              alignItems: "center",
              position: "relative",
              pb: hasArcResults ? 2 : 10,
              overflow: "hidden",
            }}
          >
            <Container
              maxWidth="md"
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              {!hasArcResults && (
                <Fade in timeout={800}>
                  <Box sx={{ mb: 4, textAlign: "center" }}>
                    <GradientText
                      variant="h4"
                      sx={{ fontWeight: 700, mb: 1, display: "inline-block" }}
                    >
                      Arc Search Research
                    </GradientText>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ maxWidth: 600, mx: "auto", mb: 1 }}
                    >
                      A recursive research engine that autonomously identifies
                      knowledge gaps, browses the internet to fill them, and
                      synthesizes details.
                    </Typography>
                  </Box>
                </Fade>
              )}

              <Box sx={{ flex: 1, width: "100%" }}>
                <ArcSearch
                  authToken={authToken}
                  onResultsChange={setHasArcResults}
                />
              </Box>
            </Container>
          </Box>
        )}
      </Box>

      {/* Hidden & Helper Components */}
      <input
        ref={cameraInputRef}
        type="file"
        hidden
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
      />
      <input
        ref={galleryInputRef}
        type="file"
        hidden
        multiple
        accept="image/*,.pdf"
        onChange={handleFileSelect}
      />

      <AnalysisModal
        open={analysisModalOpen}
        onClose={handleCloseAnalysisModal}
        deepAnalysis={deepAnalysis}
        deepDive={deepDive}
        deeperDive={deeperDive}
        loading={loading}
        onDeepAnalysisToggle={handleDeepAnalysisToggle}
        onDeepDiveToggle={handleDeepDiveToggle}
        onDeeperDiveToggle={handleDeeperDiveToggle}
      />

      <Menu
        anchorEl={fileMenuAnchor}
        open={Boolean(fileMenuAnchor)}
        onClose={handleFileMenuClose}
        PaperProps={{ sx: { borderRadius: "12px", mt: 1, minWidth: 200 } }}
      >
        <MenuItem onClick={handleCameraClick}>
          <ListItemIcon>
            <CameraAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Camera</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleGalleryClick}>
          <ListItemIcon>
            <PhotoLibraryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Photos</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleGalleryClick}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>PDF Documents</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={imageViewerOpen}
        onClose={handleImageViewerClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            bgcolor: "transparent",
            boxShadow: "none",
          },
        }}
        BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.9)" } }}
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={handleImageViewerClose}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              bgcolor: alpha(theme.palette.common.black, 0.5),
              "&:hover": { bgcolor: alpha(theme.palette.common.black, 0.7) },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          {viewerImage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "60vh",
              }}
            >
              <img
                src={viewerImage}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "85vh",
                  objectFit: "contain",
                  borderRadius: "12px",
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={error.open}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity={error.isServerError ? "error" : "warning"}
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {error.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
